import React from 'react';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';

import { ChatMessage, chatWithAgent } from '../api/ExplainabilityApi';

export interface AgentChatProps {
  results: unknown[];
  metadata: Record<string, string>;
}

const CHAT_TIMEOUT_MS = 30_000;

export default function AgentChat({ results, metadata }: AgentChatProps): JSX.Element {
  const [expanded, setExpanded] = React.useState(false);
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sessionId, setSessionId] = React.useState<string | undefined>(undefined);
  const [initialized, setInitialized] = React.useState(false);

  const abortRef = React.useRef<AbortController | null>(null);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = React.useRef<HTMLDivElement | null>(null);

  // Reset chat when results change
  React.useEffect(() => {
    setMessages([]);
    setSessionId(undefined);
    setInitialized(false);
    setExpanded(false);
    setError(null);
    setInputValue('');
  }, [results]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Scroll to bottom when messages change
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (content: string, currentMessages: ChatMessage[]) => {
    abortRef.current?.abort();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const userMessage: ChatMessage = { role: 'user', content };
    const updatedMessages = [...currentMessages, userMessage];
    setMessages(updatedMessages);
    setLoading(true);
    setError(null);

    timeoutRef.current = setTimeout(() => {
      controller.abort();
    }, CHAT_TIMEOUT_MS);

    try {
      const response = await chatWithAgent(
        updatedMessages,
        results,
        metadata,
        sessionId,
        controller.signal
      );

      if (controller.signal.aborted) return;

      setSessionId(response.session_id);
      const assistantMessage: ChatMessage = { role: 'assistant', content: response.content };
      setMessages([...updatedMessages, assistantMessage]);
    } catch (err) {
      if (controller.signal.aborted && loading) {
        setError('Response timed out. Please try again.');
        return;
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to get response';
      setError(errorMessage);
    } finally {
      setLoading(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  const handleExpand = (isExpanded: boolean) => {
    setExpanded(isExpanded);
    // Auto-send first message when opened for the first time
    if (isExpanded && !initialized) {
      setInitialized(true);
      sendMessage('Explain the results', []);
    }
  };

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || loading) return;
    setInputValue('');
    sendMessage(trimmed, messages);
  };

  const handleKeyDown = (event: CustomEvent<{ key: string }>) => {
    if (event.detail.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <ExpandableSection
      variant="container"
      headerText="💬 Chat with AI Agent"
      headerDescription="Ask follow-up questions about the attribution results"
      expanded={expanded}
      onChange={({ detail }) => handleExpand(detail.expanded)}
    >
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '300px', maxHeight: '500px' }}>
        {/* Messages area */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '12px',
            marginBottom: '12px',
            border: '1px solid #e9ebed',
            borderRadius: '8px',
            backgroundColor: '#fafafa',
          }}
        >
          <SpaceBetween size="s">
            {messages.map((msg, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    backgroundColor: msg.role === 'user' ? '#0972d3' : '#ffffff',
                    color: msg.role === 'user' ? '#ffffff' : '#000716',
                    border: msg.role === 'assistant' ? '1px solid #e9ebed' : 'none',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  <Box variant="p" color={msg.role === 'user' ? 'inherit' : undefined}>
                    {msg.content}
                  </Box>
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <StatusIndicator type="loading">Thinking…</StatusIndicator>
              </div>
            )}
            {error && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <StatusIndicator type="error">{error}</StatusIndicator>
              </div>
            )}
            <div ref={messagesEndRef} />
          </SpaceBetween>
        </div>

        {/* Input area */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <Input
              value={inputValue}
              onChange={({ detail }) => setInputValue(detail.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about the attribution results…"
              disabled={loading}
              ariaLabel="Chat message input"
            />
          </div>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={!inputValue.trim() || loading}
            iconName="send"
            ariaLabel="Send message"
          >
            Send
          </Button>
        </div>
      </div>
    </ExpandableSection>
  );
}
